// AudioHelper & AudioWrapper (Web Audio API kullanılarak, medya kontrolüne takılmadan ses oynatma)

import { audioList } from "~constants";

import LockHelper from "./LockHelper";

class AudioWrapper {
  _context = new (window.AudioContext || window.webkitAudioContext)();
  _buffer = null;
  _source = null;
  _isPlaying = false;
  _audioPath = null;

  constructor(wav) {
    this._audioPath = wav;
    AudioHelper.isAudioAvailable().then(async (isAvailable) => {
      if (isAvailable) {
        const response = await fetch(wav);
        const data = await response.arrayBuffer();
        this._buffer = await this._context.decodeAudioData(data);
      }
    });
  }

  play = async () => {
    const isAvailable = await LockHelper.acquireLockWithinMs(this._audioPath, 250);
    if (!isAvailable || !this._buffer) return;

    try {
      if (this._context.state === "suspended") {
        try {
          await this._context.resume();
        } catch (err) {
          console.warn("AudioContext resume failed. Notification sound will not be played.");
          return;
        }
      }

      this._isPlaying = true;
      this._source = this._context.createBufferSource();
      this._source.buffer = this._buffer;
      this._source.connect(this._context.destination);
      this._source.start(0);
      this._source.onended = () => {
        this._isPlaying = false;
      };
    } catch {}
  };

  replay = async () => {
    return this.play();
  };

  stop = () => {
    if (this._source) {
      try {
        this._source.stop();
        this._isPlaying = false;
      } catch {}
    }
  };

  stopAndReset = async () => {
    this.stop();
  };

  isPlaying = () => {
    return this._isPlaying;
  };

  pause = async () => {
    this.stop();
  };
}

export default class AudioHelper {
  static _audioList = {};

  static async loadRecognitionSound(loadKey = "") {
    switch (loadKey) {
      case audioList.recognition: {
        const { default: wav } = await import("~assets/sounds/recognize-start.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.recognitionError: {
        const { default: wav } = await import("~assets/sounds/recognize-error.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.agentWaitingAlert: {
        const { default: wav } = await import("~assets/sounds/agent-waiting-alert.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.agentNewMessage: {
        const { default: wav } = await import("~assets/sounds/new-message.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.customerNewMessage: {
        const { default: wav } = await import("~assets/sounds/customer-new-message.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.noSound: {
        const { default: wav } = await import("~assets/sounds/no-sound.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.agentTransferred: {
        const { default: wav } = await import("~assets/sounds/agent-transferred.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      case audioList.customerAgentJoined: {
        const { default: wav } = await import("~assets/sounds/customer-agent-joined.mp3");
        this._audioList[loadKey] = new AudioWrapper(wav);
        break;
      }
      default:
        break;
    }
  }

  static async get(key) {
    if (!this._audioList[key]) {
      await this.loadRecognitionSound(key);
    }
    return this._audioList[key];
  }

  static async isMicAvailable() {
    if (!navigator?.mediaDevices) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      return stream.getAudioTracks().length > 0;
    } catch {
      return false;
    }
  }

  static async isAudioAvailable() {
    return typeof window?.AudioContext !== "undefined" || typeof window?.webkitAudioContext !== "undefined";
  }

  static _clickListenerAttached = false;
  static listenClickAndReady() {
    if (this._clickListenerAttached) return;

    const handler = async () => {
      try {
        const audioContextModule = window.AudioContext || window.webkitAudioContext;
        const context = new audioContextModule();
        if (context.state === "suspended") {
          await context.resume();
        }
      } catch (err) {
        console.warn("AudioContext interaction resume failed", err);
      }
      window.removeEventListener("click", handler);
      window.removeEventListener("touchstart", handler);
      this._clickListenerAttached = false;
    };

    window.addEventListener("click", handler, { once: true });
    window.addEventListener("touchstart", handler, { once: true });
    this._clickListenerAttached = true;
  }
}
